import type { ReactElement } from 'react';

export interface CompileGateBannerProps {
  readonly totalDocs: number;
  readonly classifiedDocs: number;
  readonly confirmedDocs: number;
  readonly unconfirmedClassifiedIds: readonly string[];
  readonly onGoToDocuments: () => void;
}

// phase-partial-compile-with-exclusions — compile is now PERMISSIVE: it
// packages the confirmed documents and records the rest as exclusions. This
// banner is now an honest WARNING (never "cannot be packaged"): it tells the
// operator how many documents would be EXCLUDED from a compile right now, so
// they can either compile a draft partial package or confirm the rest for a
// complete, submittable one. It stays hidden when nothing would be excluded.
export function CompileGateBanner({
  totalDocs: _totalDocs,
  classifiedDocs: _classifiedDocs,
  confirmedDocs: _confirmedDocs,
  unconfirmedClassifiedIds,
  onGoToDocuments,
}: CompileGateBannerProps): ReactElement | null {
  const n = unconfirmedClassifiedIds.length;
  if (n === 0) return null;

  const plural = n === 1 ? '' : 's';
  const willWould = n === 1 ? 'will' : 'will';

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="compile-gate-banner"
      style={{
        margin: '12px 0',
        padding: '12px 16px',
        background: '#FFF7ED',
        border: '1px solid #FDBA74',
        borderRadius: 6,
        color: '#9A3412',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 240px', minWidth: 240 }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>
          ⚠️ {n} document{plural} {willWould} be excluded from the compile
        </div>
        <div style={{ marginTop: 4, fontSize: 13 }}>
          {n} classified document{plural} {n === 1 ? 'is' : 'are'} not yet confirmed.
          You can compile a draft package now — {n === 1 ? 'it' : 'they'} will be left
          out and listed as {n === 1 ? 'an exclusion' : 'exclusions'}. Confirm{' '}
          {n === 1 ? 'it' : 'them'} for a complete package you can submit.
        </div>
      </div>
      <button
        type="button"
        onClick={onGoToDocuments}
        style={{
          background: '#9A3412',
          color: '#fff',
          border: 'none',
          borderRadius: 4,
          padding: '8px 14px',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Go to Documents tab
      </button>
    </div>
  );
}
